<template>
  <div>
    <v-container fluid class="d-flex" style="min-height: 90vh" fill-height>
      <v-row align="center" justify="center">
        <v-col cols="12">
          <v-card class="rounded-xl elevation-4 pa-6">
            <v-card-title class="justify-center pb-6">
              <span class="text-h5 font-weight-bold text-primary">{{
                $t("message.ParkingWalkIn")
              }}</span>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pt-6">
              <v-container>
                <v-row justify="center" dense>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="WorkPermitNo" :label="$t('message.WorkPermitNumber')" outlined dense
                      clearable prepend-inner-icon="mdi-briefcase-outline" />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="Subject" :label="$t('message.Subject')" outlined dense clearable
                      prepend-inner-icon="mdi-calendar-text" />
                  </v-col>
                </v-row>
                <v-row justify="center" dense>
                  <v-col cols="12" md="3">
                    <v-autocomplete v-model="ProjectSite" :items="ProjectSiteList" item-text="projectName"
                      item-value="projectID" outlined dense clearable prepend-inner-icon="mdi-map-marker">
                      <template v-slot:label>
                        {{ $t("message.ProjectSite") }}
                        <span style="color: red">*</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                </v-row>
                <v-row justify="center" dense>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="VisitorName" outlined dense clearable prepend-inner-icon="mdi-account">
                      <template v-slot:label>
                        {{ $t("message.FirstName") }}
                        <span style="color: red">*</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="VisitorLastName" outlined dense clearable prepend-inner-icon="mdi-account">
                      <template v-slot:label>
                        {{ $t("message.LastName") }}
                        <span style="color: red">*</span>
                      </template>
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row justify="center" dense>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="Phone"
                      outlined
                      dense
                      type="tel"
                      inputmode="numeric"
                      maxlength="10"
                      clearable
                      prepend-inner-icon="mdi-phone"
                      @keydown="allowPhoneKeydown"
                      @paste.prevent="Phone = filterPhoneInput($event.clipboardData.getData('text'))"
                      @input="Phone = filterPhoneInput($event)"
                    >
                      <template v-slot:label>
                        {{ $t("message.PhoneNumber") }}
                        <span style="color: red">*</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="Email"
                      :label="$t('message.Email')"
                      outlined
                      dense
                      :rules="emailRules"
                      clearable
                      prepend-inner-icon="mdi-email"
                    />
                  </v-col>
                </v-row>
                <v-row justify="center" dense>
                  <v-col cols="12" md="3">
                    <v-menu v-model="menuStartDate" :close-on-content-click="false" transition="scale-transition"
                      offset-y min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="StartDateDisplay"
                          prepend-inner-icon="mdi-calendar"
                          outlined
                          dense
                          disabled
                          readonly
                          clearable
                          v-bind="attrs"
                          v-on="on"
                        >
                          <template v-slot:label>
                            {{ $t("message.StartDate") }}
                            <span style="color: red">*</span>
                          </template>
                        </v-text-field>
                      </template>

                      <v-date-picker v-model="StartDate" :min="today" :max="today" @change="menuStartDate = false" />
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu ref="menuStartTime" v-model="menuStartTime" :close-on-click="!StartTimePickerDirty" :close-on-content-click="false"
                      @input="onStartTimeMenuInput"
                      transition="scale-transition" offset-x :nudge-right="8" max-width="290px"
                      min-width="290px">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="StartTime" prepend-inner-icon="mdi-clock-time-four-outline" outlined
                          dense placeholder="HH:mm" maxlength="5" inputmode="numeric" v-bind="attrs" v-on="on"
                          @keydown="allowTimeKeydown" @paste.prevent="onStartTimeInput($event.clipboardData.getData('text'))"
                          @input="onStartTimeInput($event)" @blur="onStartTimeBlur">
                          <template v-slot:label>
                            {{ $t("message.StartTime") }}
                            <span style="color: red">*</span>
                          </template>
                        </v-text-field>
                      </template>
                      <v-time-picker v-if="menuStartTime" v-model="StartTimePicker" full-width format="24hr"
                        @input="onStartTimePickerInput"
                        @click:hour="onStartTimeHourClick"
                        @click:minute="onStartTimeMinuteClick" />
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row justify="center" dense>
                  <v-col cols="12" md="3">
                    <v-menu v-model="menuEndDate" :close-on-content-click="false" transition="scale-transition" offset-y
                      min-width="auto">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field
                          v-model="EndDateDisplay"
                          prepend-inner-icon="mdi-calendar"
                          outlined
                          dense
                          readonly
                          disabled
                          clearable
                          v-bind="attrs"
                          v-on="on"
                        >
                          <template v-slot:label>
                            {{ $t("message.EndDate") }}
                            <span style="color: red">*</span>
                          </template>
                        </v-text-field>
                      </template>
                      <v-date-picker v-model="EndDate" :min="today" :max="today" @change="menuEndDate = false" />
                    </v-menu>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-menu ref="menuEndTime" v-model="menuEndTime" :close-on-click="!EndTimePickerDirty" :close-on-content-click="false"
                      @input="onEndTimeMenuInput"
                      transition="scale-transition" offset-x :nudge-right="8" max-width="290px"
                      min-width="290px">
                      <template v-slot:activator="{ on, attrs }">
                        <v-text-field v-model="EndTime" prepend-inner-icon="mdi-clock-time-four-outline" outlined dense
                          placeholder="HH:mm" maxlength="5" inputmode="numeric" v-bind="attrs" v-on="on"
                          @keydown="allowTimeKeydown" @paste.prevent="onEndTimeInput($event.clipboardData.getData('text'))"
                          @input="onEndTimeInput($event)" @blur="onEndTimeBlur">
                          <template v-slot:label>
                            {{ $t("message.EndTime") }}
                            <span style="color: red">*</span>
                          </template>
                        </v-text-field>
                      </template>
                      <v-time-picker
                        v-if="menuEndTime"
                        v-model="EndTimePicker"
                        full-width
                        format="24hr"
                        @input="onEndTimePickerInput"
                        @click:hour="onEndTimeHourClick"
                        @click:minute="onEndTimeMinuteClick"
                        :allowed-hours="(hour) => hour !== 0"
                        :allowed-minutes="(minute) => true"
                      />
                    </v-menu>
                  </v-col>
                </v-row>
                <v-row justify="center" dense>
                  <v-col cols="12" md="3">
                    <v-text-field v-model="LicensePlate" outlined dense clearable prepend-inner-icon="mdi-car"
                      @input="onLicensePlateInput">
                      <template v-slot:label>
                        {{ $t("message.LicensePlate") }}
                        <span style="color: red">*</span>
                      </template>
                    </v-text-field>
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select v-model="VehicleType" :items="['Car', 'Motorcycle']" outlined dense
                      prepend-inner-icon="mdi-truck">
                      <template v-slot:label>
                        {{ $t("message.VehicleType") }}
                        <span style="color: red">*</span>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
                <v-row justify="center" dense>
                  <v-col cols="12" md="3">
                    <v-autocomplete v-model="Province" :items="ProvinceList" item-text="provinceName"
                      item-value="provinceID" outlined dense clearable prepend-inner-icon="mdi-map-marker">
                      <template v-slot:label>
                        {{ $t("message.Province") }}
                        <span style="color: red">*</span>
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex justify-center">
                    <v-btn color="#ff7f00" width="130" class="mr-4 white--text rounded-lg" large
                      @click="getDataThaiIDCard()">{{
                        $t("message.GetThaiID") }}</v-btn>

                    <v-btn color="#4254f7" width="130" class="white--text rounded-lg" large
                      @click="getDataPassport()">{{
                        $t("message.GetPassport") }}</v-btn>
                  </v-col>
                </v-row>
                <v-row justify="center" class="mt-6">
                  <v-col cols="12" md="4" class="d-flex justify-center">
                    <v-btn color="primary" width="180" class="mr-4 white--text rounded-lg" large
                      @click="OpenSummaryDialog()">{{
                        $t("message.Next") }}</v-btn>

                    <v-btn color="error" width="180" class="white--text rounded-lg" large @click="BackToMainPage()">{{
                      $t("message.Cancel") }}</v-btn>
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="SummaryDialog" persistent max-width="800px">
      <v-card class="rounded-xl elevation-6">
        <v-card-title class="d-flex align-center justify-space-between py-4" style="
            background: linear-gradient(90deg, #1976d2, #42a5f5);
            color: white;
          ">
          <span class="text-h6 font-weight-bold">{{
            $t("message.VisitorSummary")
          }}</span>
          <v-btn icon variant="text" @click="CloseSummaryDialog()" style="color: white">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-6">
          <v-simple-table class="mb-6 rounded-lg elevation-1">
            <template v-slot:default>
              <tbody>
                   <tr>
                  <td class="text-subtitle-2 font-weight-medium">{{ $t('message.Subject') }}</td>
                  <td class="text-right">
                    {{ Subject }}
                  </td>
                </tr>
                <tr>
                  <td class="text-subtitle-2 font-weight-medium text-primary">
                    {{ $t("message.FullName") }}
                  </td>
                  <td class="text-right">
                    {{ VisitorName }} {{ VisitorLastName }}
                  </td>
                </tr>
                <tr>
                  <td class="text-subtitle-2 font-weight-medium text-primary">
                    {{ $t("message.PhoneNumber") }}
                  </td>
                  <td class="text-right">{{ Phone }}</td>
                </tr>
                <tr>
                  <td class="text-subtitle-2 font-weight-medium text-primary">
                    {{ $t("message.Email") }}
                  </td>
                  <td class="text-right">{{ Email }}</td>
                </tr>
                <tr>
                  <td class="text-subtitle-2 font-weight-medium text-primary">
                    {{ $t("message.Duration") }}
                  </td>
                  <td class="text-right">
                    {{
                      StartDate && EndDate
                        ? ChangeFormatDate(StartDate) +
                        " " +
                        StartTime +
                        " - " +
                        ChangeFormatDate(EndDate) +
                        " " +
                        EndTime
                        : ""
                    }}
                  </td>
                </tr>
                <tr>
                  <td class="text-subtitle-2 font-weight-medium text-primary">
                    {{ $t("message.LicensePlate") }}
                  </td>
                  <td class="text-right">{{ LicensePlate }}</td>
                </tr>
                <tr>
                  <td class="text-subtitle-2 font-weight-medium text-primary">
                    {{ $t("message.VehicleType") }}
                  </td>
                  <td class="text-right">{{ VehicleType }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-row justify="center">
            <v-col cols="12" md="4" class="d-flex">
              <v-btn block color="success" class="rounded-lg elevation-2" large @click="CreateWalkInParking()">{{
                $t("message.Submit") }}</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="ShowImageDialog" persistent max-width="900px">
      <v-card class="pa-6 rounded-xl">
        <v-card-text>
          <v-row>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-card id="QRCode" elevation="6" class="rounded-xl pb-16" style="
                  width: 380px;
                  min-height: 450px;
                  background-color: #fff;
                  padding: 55px;
                ">
                <div class="d-flex justify-center">
                  <v-img alt="Logo" class="shrink app" contain src="@/assets/logo_chememan.svg"
                    transition="scale-transition" width="300" />
                </div>
                <div class="d-flex justify-center mt-10">
                  <qrcode-vue :value="QRvalue" :size="QRsize" level="M" />
                </div>
                <v-row class="mt-5 text-body-1 font-weight-medium justify-center" style="padding: 5px">
                </v-row>
                <v-row class="text-body-1 font-weight-bold justify-center">
                  <span>{{ QRVisitorName }} {{ QRVisitorLastName }}</span>
                </v-row>
                <v-row class="mt-5 text-body-1 font-weight-medium justify-center">
                  <span>{{ QRLicensePlate }} {{ QRProvince }}</span>
                </v-row>
                <v-row class="mt-4 text-body-1 font-weight-medium justify-center">
                  <span>{{ QRVehicleType }}</span>
                </v-row>
                <v-row class="mt-6 text-body-1 font-weight-medium justify-space-between">
                  <span>StartDate: {{ ChangeFormatDateQR(QRDate) }}</span>
                  <span>{{ ChangeFormatTimeQR(QRTime) }}</span>
                </v-row>
                <v-row class="mt-4 text-body-1 font-weight-medium justify-space-between">
                  <span>ExpireDate: {{ ChangeFormatDateQR(QRExpireDate) }}</span>
                  <span>{{ ChangeFormatTimeQR(QRExpireTime) }}</span>
                </v-row>

                <v-row class="mt-4 text-body-1 font-weight-medium justify-center">
                  <span>{{ QRSubject }}</span>
                </v-row>
                <v-row class="justify-center">
                  <span style="
                      width: 300px;
                      border: 1px solid black;
                      display: inline-block;
                      margin-top: 100px;
                      margin-bottom: 10px;
                    "></span>
                </v-row>
                <v-row class="text-body-1 font-weight-medium justify-center">
                  <span>ผู้รับการติดต่อ</span>
                </v-row>
                <v-row class="text-body-1 font-weight-medium justify-center">
                  <span style="font-size: 12px; margin-top: 40px">หมายเหตุ: กรุณาเก็บ QR Code นี้ไว้สแกนขาออก</span>
                </v-row>
                <v-card elevation="0">
                  <p style="margin-top: 30px">
                    <span style="color: gray">.</span>
                  </p>
                </v-card>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" class="d-flex flex-column align-center justify-center">
              <v-btn color="success" class="ma-4 pa-6 rounded-xl d-flex" style="width: 160px; height: 160px"
                @click="PrintQRParking()">
                <v-icon size="28">mdi-printer</v-icon>
                <div class="text-subtitle-1 font-weight-medium">PRINT</div>
              </v-btn>

              <v-btn color="grey darken-1" class="ma-4 pa-6 rounded-xl d-flex" style="width: 160px; height: 160px"
                @click="ClosePrintQRDialog()">
                <v-icon size="28">mdi-close-circle-outline</v-icon>
                <div class="text-subtitle-1 font-weight-medium">CLOSE</div>
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="LoadingDialog" persistent width="auto">
      <v-card class="pa-6 d-flex flex-column align-center justify-center" elevation="8" rounded="xl">
        <v-progress-circular indeterminate color="indigo" size="70" width="6"></v-progress-circular>
        <span class="mt-4 text-subtitle-1 font-weight-medium">Loading, please wait...</span>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import axios from "axios";
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import QrcodeVue from "qrcode.vue";
import html2canvas from "html2canvas";
export default {
  name: "WalkInParking",
  components: {
    QrcodeVue,
  },
  data() {
    return {
      url: enurl.apiUrl,
      urlReader: enurl.apiReaderUrl,
      LoadingDialog: false,
      QRvalue: "",
      QRsize: 120,
      Username: this.$cookies.get("Username"),
      menuStartDate: false,
      menuEndDate: false,
      menuStartTime: false,
      menuEndTime: false,
      today: new Date().toISOString().substr(0, 10),

      visitorID: null,
      WorkPermitNo: "",
      Subject: "",
      VisitorName: "",
      VisitorLastName: "",
      Phone: "",
      Email: "",
      emailRules: [ 
        v => !v || /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'E-mail must be valid'
      ],
      StartDate: new Date().toISOString().slice(0,10),
      EndDate: new Date().toISOString().slice(0,10),
      StartTime: "07:00",
      EndTime: "23:00",
      StartTimePicker: "07:00",
      EndTimePicker: "23:00",
      StartTimePickerDirty: false,
      EndTimePickerDirty: false,
      ProvinceList: [],
      Province: null,
      LicensePlate: "",
      VehicleType: "",
      SummaryDialog: false,
      EmailApproveList: [],
      ShowImageDialog: false,
      QRImage: "",
      QRLicensePlate: "",
      QRProvince: "",
      QRVehicleType: "",
      QRSubject: "",
      ProjectSite: null,
      ProjectSiteList: [],

      QRDate: "",
      QRTime: "",
      QRExpireDate: "",
      QRExpireTime: "",
      QRVisitorName: "",
      QRVisitorLastName: "",
      Base64: "",
      dialogText:
        "Parking Payment Options<br/>1. Mobile Payment Scan QR code through mobile camera<br/>or One Bangkok application<br/>2. Kiosk Payment Scan QR cade at parking payment kiosk located at retail area<br/><br/>วิธีชำระค่าบริการ<br/>1. Mobile Payment สแกนQR codeด้านบนผ่านโทรศัพท์มือถือ หรือ<br/>One Bangkok แอพพลิเคชั่น<br/>2. Kiosk Payment นำบัตรจอดรถแสกนที่ตู้อัตโนมัติภายในศูนย์ฯ",
    };
  },

  async mounted() {
    let self = this;
    self.getDataMasterProvince();
    self.getDataDropdownProjectSite();
  },

  computed: {
    StartDateDisplay() {
      if (!this.StartDate) return "";
      let [year, month, day] = this.StartDate.split("-");
      return `${day}-${month}-${year}`;
    },
    EndDateDisplay() {
      if (!this.EndDate) return "";
      let [year, month, day] = this.EndDate.split("-");
      return `${day}-${month}-${year}`;
    },
  },

  methods: {
    filterPhoneInput(value) {
      if (!value) return "";
      return value.replace(/\D/g, "").slice(0, 10);
    },

    allowPhoneKeydown(event) {
      const controlKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "Escape",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ];

      if (controlKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
        return;
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
        return;
      }

      const input = event.target;
      const nextValue =
        input.value.slice(0, input.selectionStart) +
        event.key +
        input.value.slice(input.selectionEnd);

      if (nextValue.replace(/\D/g, "").length > 10) {
        event.preventDefault();
      }
    },

    filterTimeInput(value) {
      if (!value) return "";
      const digits = value.replace(/\D/g, "").slice(0, 4);
      if (digits.length <= 2) return digits;

      const hour = Number(digits.slice(0, 2)) > 23 ? digits.charAt(0) : digits.slice(0, 2);
      if (hour.length < 2) return hour;

      let minute = digits.slice(2);
      if (Number(minute.charAt(0)) > 5) minute = "";

      return `${hour}:${minute}`;
    },

    allowTimeKeydown(event) {
      const controlKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Enter",
        "Escape",
        "ArrowLeft",
        "ArrowRight",
        "Home",
        "End",
      ];

      if (controlKeys.includes(event.key) || event.ctrlKey || event.metaKey) {
        return;
      }

      if (!/^\d$/.test(event.key)) {
        event.preventDefault();
        return;
      }

      const input = event.target;
      const nextValue =
        input.value.slice(0, input.selectionStart) +
        event.key +
        input.value.slice(input.selectionEnd);
      const digits = nextValue.replace(/\D/g, "");

      if (digits.length > 4) {
        event.preventDefault();
        return;
      }

      if (digits.length >= 2 && Number(digits.slice(0, 2)) > 23) {
        event.preventDefault();
        return;
      }

      if (digits.length >= 3 && Number(digits.charAt(2)) > 5) {
        event.preventDefault();
      }
    },

    normalizeTime(value) {
      if (!value) return "";
      const text = value.trim();
      let hour = null;
      let minute = null;

      if (/^\d{1,2}$/.test(text)) {
        hour = Number(text);
        minute = 0;
      } else if (/^\d{3,4}$/.test(text)) {
        hour = Number(text.slice(0, -2));
        minute = Number(text.slice(-2));
      } else {
        const match = text.match(/^(\d{1,2}):(\d{1,2})$/);
        if (!match) return text;
        hour = Number(match[1]);
        minute = Number(match[2]);
      }

      if (hour > 23 || minute > 59) return text;

      return `${hour.toString().padStart(2, "0")}:${minute
        .toString()
        .padStart(2, "0")}`;
    },

    isValidTime(value) {
      return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
    },

    getValidTime(value) {
      const time = this.normalizeTime(value);
      return this.isValidTime(time) ? time : "";
    },

    async onLicensePlateInput(value) {
      const text = value || "";
      if (/[^ก-ฮ0-9a-zA-Z]/.test(text)) {
        await Swal.fire({
          icon: "warning",
          width: 600,
          text: "ป้ายทะเบียนต้องไม่มีช่องว่างหรืออักขระพิเศษ !",
        });
      }
      this.LicensePlate = text.replace(/[^ก-ฮ0-9a-zA-Z]/g, "");
    },

    onStartTimeInput(value) {
      this.StartTimePickerDirty = true;
      this.StartTime = this.filterTimeInput(value);
      const time = this.getValidTime(this.StartTime);
      if (time) this.StartTimePicker = time;
    },

    onEndTimeInput(value) {
      this.EndTimePickerDirty = true;
      this.EndTime = this.filterTimeInput(value);
      const time = this.getValidTime(this.EndTime);
      if (time) this.EndTimePicker = time;
    },

    onStartTimeBlur() {
      this.StartTime = this.normalizeTime(this.StartTime);
      const time = this.getValidTime(this.StartTime);
      if (time) this.StartTimePicker = time;
    },

    onEndTimeBlur() {
      this.EndTime = this.normalizeTime(this.EndTime);
      const time = this.getValidTime(this.EndTime);
      if (time) this.EndTimePicker = time;
    },

    onStartTimePickerInput(value) {
      this.StartTimePickerDirty = true;
      this.StartTime = value;
      this.StartTimePicker = value;
    },

    onEndTimePickerInput(value) {
      this.EndTimePickerDirty = true;
      this.EndTime = value;
      this.EndTimePicker = value;
    },

    onStartTimeHourClick() {
      this.StartTimePickerDirty = true;
    },

    onEndTimeHourClick() {
      this.EndTimePickerDirty = true;
    },

    onStartTimeMinuteClick() {
      this.onStartTimePickerInput(this.StartTimePicker);
      this.menuStartTime = false;
    },

    onEndTimeMinuteClick() {
      this.onEndTimePickerInput(this.EndTimePicker);
      this.menuEndTime = false;
    },

    onStartTimeMenuInput(isOpen) {
      if (isOpen) {
        this.StartTimePickerDirty = false;
        this.menuEndTime = false;
      } else {
        this.StartTimePickerDirty = false;
      }
    },

    onEndTimeMenuInput(isOpen) {
      if (isOpen) {
        this.EndTimePickerDirty = false;
        this.menuStartTime = false;
      } else {
        this.EndTimePickerDirty = false;
      }
    },

    CreateWalkInParking() {
      let self = this;
      self.LoadingDialog = true;
      let temp = {
        workpermit: self.WorkPermitNo,
        subject: self.Subject,
        firstname: self.VisitorName,
        lastname: self.VisitorLastName,
        phone: self.Phone,
        email: self.Email,
        startDate: self.StartDate,
        endDate: self.EndDate,
        startTime: self.StartTime,
        endTime: self.EndTime,
        licensePlate: self.LicensePlate,
        province: self.Province,
        vehicleType: self.VehicleType,
        emailApprove: self.EmailApprove,
        preRegisterType: 2,
        visitorType: 2,
        projectID: self.ProjectSite
      };
      axios
        .post(`${self.url}PreRegister/CreatePreRegister`, temp)
        .then(function (response) {
          if (response.data.status == 0) {
            self.visitorID = response.data.data.visitorID;
            self.QRLicensePlate = response.data.data.licensePlate;
            self.QRProvince = response.data.data.province;
            self.QRVehicleType = response.data.data.vehicleType;
            self.QRDate = response.data.data.startDate;
            self.QRTime = response.data.data.startDate;
            self.QRVisitorName = response.data.data.visitorName;
            self.QRVisitorLastName = response.data.data.visitorLastName;
            self.QRExpireDate = response.data.data.expireDate;
            self.QRExpireTime = response.data.data.expireDate;
            self.QRSubject = response.data.data.subject;
            Swal.fire({
              icon: "success",
              title: "Success",
              showConfirmButton: false,
              timer: 1500,
            }).then(() => {
              self.CloseSummaryDialog();
              self.OpenQrImage(
                "https://smartgate-carpark-api.chememan.com/" + self.visitorID
              );
            });
          }
          else if (response.data.status == 1) {
            Swal.fire({
              icon: "warning",
              width: 900,
              text: response.data.message,
            });
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: "Error...",
            width: 900,
            text: error,
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    PrintQRParking() {
      const el = document.querySelector("#QRCode");
      html2canvas(el, { backgroundColor: "#ffffff", scale: 2 }).then(
        (canvas) => {
          const base64 = canvas.toDataURL("image/png");

          if (
            typeof window.Android !== "undefined" &&
            window.Android.printBase64Image
          ) {
            // console.log("Printing via Android bridge...");
            window.Android.printBase64Image(base64);
          } else {
            var tWindow = window.open("", "_blank");
            tWindow.document.write(
              "<html><head><title>Print</title></head><body style='margin:0;padding:0;text-align:center;background:white;'>"
            );
            tWindow.document.body.appendChild(canvas);
            tWindow.document.write("</body></html>");
            tWindow.document.close();
            tWindow.focus();
            setTimeout(() => {
              tWindow.print();
              tWindow.close();
            }, 500);
          }

          this.Base64 = base64;
        }
      );
    },

    getDataMasterProvince() {
      let self = this;
      axios
        .get(`${self.url}Management/getDataMasterProvince`)
        .then(function (response) {
          if (response.data.status == 0) {
            self.ProvinceList = response.data.data;
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: "Error...",
            width: 900,
            text: error,
          });
        });
    },

    getDataDropdownProjectSite() {
      let self = this;
      axios
        .get(`${self.url}Management/getDataDropdownProjectSite`)
        .then(function (response) {
          if (response.data.status == 0) {
            self.ProjectSiteList = response.data.data;
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: "Error...",
            width: 900,
            text: error,
          });
        });
    },

    getDataEmailListForApprove() {
      let self = this;
      axios
        .get(`${self.url}Management/getDataEmailListForApprove`)
        .then(function (response) {
          if (response.data.status == 0) {
            self.EmailApproveList = response.data.data;
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: "Error...",
            width: 900,
            text: error,
          });
        });
    },

    getDataThaiIDCard() {
      let self = this;
      self.LoadingDialog = true;
      axios
        .get(`${self.urlReader}SmartCardReader/ThaiSmartCardReader`)
        .then(function (response) {
          if (response.data.status == 0) {
            self.VisitorName = response.data.data.firstname;
            self.VisitorLastName = response.data.data.lastname;
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: "Error...",
            width: 900,
            text: error,
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    getDataPassport() {
      let self = this;
      self.LoadingDialog = true;
      axios
        .get(`${self.urlReader}PassportReader/PassportReader`)
        .then(function (response) {
          if (response.data.status == 0) {
            self.VisitorName = response.data.data.firstname;
            self.VisitorLastName = response.data.data.lastname;
          }
        })
        .catch(function (error) {
          Swal.fire({
            icon: "error",
            title: "Error...",
            width: 900,
            text: error,
          });
        })
        .finally(() => {
          self.LoadingDialog = false;
        });
    },

    OpenSummaryDialog() {
      let self = this;
      self.StartTime = self.normalizeTime(self.StartTime);
      self.EndTime = self.normalizeTime(self.EndTime);
      if (
        self.VisitorName == "" ||
        self.VisitorLastName == "" ||
        self.StartDate == "" ||
        self.StartTime == "" ||
        self.EndDate == "" ||
        self.EndTime == "" ||
        self.LicensePlate == "" ||
        self.VehicleType == "" ||
        self.Province == null ||
        self.Phone == "" ||
        self.Phone == null    
      ) {
        Swal.fire({
          icon: "warning",
          width: 600,
          text: "กรุณากรอกข้อมูลผู้มาติดต่อให้ครบถ้วน !",
        });
      }
      else if(!self.isValidTime(self.StartTime) || !self.isValidTime(self.EndTime)) {
        Swal.fire({
          icon: "warning",
          width: 600,
          text: "รูปแบบเวลาต้องเป็น HH:mm เช่น 08:00 หรือ 23:00 !",
        });
      }
      else if((self.Email != "" && self.Email != null) && self.CheckformatEmail(self.Email) === false) {
        Swal.fire({
          icon: "warning",
          width: 600,
          text: "รูปแบบอีเมลไม่ถูกต้อง !",
        });
      }
      else if(self.EndTime <= self.StartTime) {
        Swal.fire({
          icon: "warning",
          width: 600,
          text: "วันที่และเวลาสิ้นสุดต้องไม่อยู่ก่อนวันที่และเวลาเริ่มต้น !",
        });
      }
      else {
        const hasSpace = /\s/;
        const hasSpecialChar = /[^ก-ฮ0-9a-zA-Z\s]/;
        if (
          hasSpace.test(self.LicensePlate) ||
          hasSpecialChar.test(self.LicensePlate)
        ) {
          Swal.fire({
            icon: "warning",
            width: 600,
            text: "ป้ายทะเบียนต้องมีเฉพาะตัวอักษรไทยและตัวเลขเท่านั้น ห้ามมีช่องว่างหรืออักขระพิเศษ !",
          });
          return;
        }
        else if(self.Phone.length != 10){
          Swal.fire({
            icon: "warning",
            width: 600,
            text: "หมายเลขโทรศัพท์ต้องมี 10 หลักเท่านั้น !",
          });
          return;
        }
        self.SummaryDialog = true;
      }
    },

    CheckformatEmail(email) {
      const emailPattern =
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      return emailPattern.test(email);
    },

    OpenQrImage(data) {
      let self = this;
      if (data != "") {
        self.QRvalue = data;
        self.ShowImageDialog = true;
      }
    },

    ClosePrintQRDialog() {
      let self = this;
      self.WorkPermitNo = "";
      self.Subject = "";
      self.VisitorName = "";
      self.VisitorLastName = "";
      self.Phone = "";
      self.Email = "";
      self.StartDate = "";
      self.EndDate = "";
      self.StartTime = "";
      self.EndTime = "";
      self.Province = null;
      self.LicensePlate = "";
      self.VehicleType = "";
      self.ProjectSite = null;
      self.ShowImageDialog = false;
      self.SummaryDialog = false;
    },

    CloseSummaryDialog() {
      let self = this;
      self.SummaryDialog = false;
    },

    BackToManagePreRegister() {
      let self = this;
      self.$router.push("/ManagePreRegister");
    },

    BackToMainPage() {
      let self = this;
      self.$router.push("/MainMenu");
    },

    ChangeFormatDate(date) {
      if (!date) return null;
      var DateData = new Date(date);
      return (
        DateData.getDate() +
        "/" +
        (DateData.getMonth() + 1) +
        "/" +
        DateData.getFullYear()
      );
    },

    ChangeFormatDateQR(date) {
      if (!date) return null;
      const DateData = new Date(date);

      const day = DateData.getDate().toString().padStart(2, "0");
      const month = (DateData.getMonth() + 1).toString().padStart(2, "0");
      const year = DateData.getFullYear();

      return `${day}/${month}/${year}`;
    },

    ChangeFormatTimeQR(date) {
      if (!date) return null;
      var DateData = new Date(date);
      var hours = DateData.getHours();
      var minutes = DateData.getMinutes();
      var seconds = DateData.getSeconds();
      minutes = minutes < 10 ? "0" + minutes : minutes;
      seconds = seconds < 10 ? "0" + seconds : seconds;
      var strTime = hours + ":" + minutes + ":" + seconds;
      return strTime;
    },
  },
};
</script>
<style scoped></style>
